import React from "react";
import NextLink from "next/link";
import { cva, type VariantProps } from "class-variance-authority";
import type { LinkData } from "@/types/link";
import type { IconName } from "@/types/icons";
import { Icon } from "./Icon";

const linkVariants = cva(
  "inline-flex items-center gap-xs transition-colors duration-200 rounded transition-all duration-300 outline-offset-0 focus-visible:outline-2 focus-visible:outline-offset-2",
  {
    variants: {
      color: {
        purple: "text-purple hover:text-purple-dark outline-purple",
        yellow: "text-yellow hover:text-yellow/80 outline-yellow",
        "purple-medium": "text-purple-medium hover:text-purple-dark",
        text: "text-inherit",
      },
    },
    defaultVariants: {
      color: "purple",
    },
  }
);

const iconVariants = cva("", {
  variants: {
    color: {
      purple: "text-purple",
      yellow: "text-yellow",
      "purple-medium": "text-purple-medium",
      text: "text-inherit",
    },
  },
  defaultVariants: {
    color: "text",
  },
});

type DataAttributes = {
  [key: `data-${string}`]: string | number | boolean | undefined;
};

type AriaAttributes = {
  [key: `aria-${string}`]: string | number | boolean | undefined;
};

export interface LinkProps
  extends
    LinkData,
    VariantProps<typeof linkVariants>,
    DataAttributes,
    AriaAttributes {
  /**
   * Optional icon to display before the label
   */
  icon?: IconName;
  /**
   * Color variant for text and icon
   * @default "purple"
   */
  color?: "purple" | "yellow" | "purple-medium" | "text";
  iconColor?: "purple" | "yellow" | "purple-medium" | "text";
  /**
   * Additional CSS classes
   */
  className?: string;
  /**
   * Optional children for custom link content
   */
  children?: React.ReactNode;
}

/**
 * Link component with optional icon and color variants
 *
 * Features:
 * - Icon support (decorative, shown before label)
 * - Color variants (purple, yellow, purple-dark)
 * - Accessibility support
 * - External link handling
 *
 * Usage:
 * ```tsx
 * <Link
 *   label="Visit website"
 *   href="https://example.com"
 *   icon="arrow-small-right"
 *   color="purple"
 *   target="_blank"
 * />
 * ```
 */
export const Link: React.FC<LinkProps> = ({
  label,
  href,
  target,
  rel,
  isActive,
  ariaLabel,
  icon,
  color = "purple",
  iconColor,
  className = "",
  children,
  ...rest
}) => {
  return (
    <NextLink
      href={href}
      target={target}
      rel={rel}
      aria-label={ariaLabel || label}
      aria-current={isActive ? "page" : undefined}
      className={linkVariants({ color, className })}
      {...rest}
    >
      {children ? (
        children
      ) : (
        <>
          {icon && (
            <Icon
              name={icon}
              size={16}
              decorative
              className={iconVariants({ color: iconColor ?? "text" })}
            />
          )}
          <span>{label}</span>
        </>
      )}
    </NextLink>
  );
};
